// ─────────────────────────────────────────────────────────────
// MarketStatusCard
// Safe-to-trade banner driven by shared calendar state from
// useCalendar (via HorizontalDashboard / Dashboard props).
//
// Props:
//   marketStatus   — "safe" | "caution" | "danger" | "unknown"
//   nextHighImpact — { event, dt, minsUntil, time } | null
//   calError       — optional message when calendar data is untrusted
//
// No internal fetch. Single source of truth is useCalendar.
//
// "unknown" is a first-class state. useCalendar returns it whenever the
// calendar payload is stale, uncovered, unavailable or malformed. It must
// never render as SAFE TO TRADE — absence of data is not absence of events.
// (Previous version fell back to statusConfig.safe for any unrecognised
// status, which turned every calendar outage into a green banner.)
// ─────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { Card } from "../../components/ui/Card";
import { C } from "../../lib/colors";

const DANGER_SECS      = 15 * 60;
const CAUTION_SECS     = 45 * 60;
const POST_FIRE_SECS   = 5 * 60;

function statusFromSeconds(diff) {
  if (diff <= 0)            return "danger";
  if (diff < DANGER_SECS)   return "danger";
  if (diff < CAUTION_SECS)  return "caution";
  return "safe";
}

export function MarketStatusCard({ marketStatus = "unknown", nextHighImpact = null, calError = null }) {
  const [timeToEvent, setTimeToEvent] = useState(null);

  // Local countdown — second-level precision between the hook's 30s status ticks
  useEffect(() => {
    if (!nextHighImpact?.dt) {
      setTimeToEvent(null);
      return;
    }

    const tick = () => {
      setTimeToEvent((nextHighImpact.dt.getTime() - Date.now()) / 1000);
    };
    tick();

    const initialDiff = (nextHighImpact.dt.getTime() - Date.now()) / 1000;
    const intervalMs  = initialDiff < 900 ? 1000 : initialDiff < 3600 ? 30000 : 60000;
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [nextHighImpact]);

  // Resolve the displayed status.
  //   - Untrusted data always wins: show UNKNOWN.
  //   - With a live countdown inside the post-fire buffer, the countdown can
  //     only make the status STRICTER than the parent's (never looser), so a
  //     local timer can't override a parent "danger" back to green.
  let status = marketStatus;
  if (marketStatus !== "unknown" && timeToEvent != null && timeToEvent > -POST_FIRE_SECS) {
    const local = statusFromSeconds(timeToEvent);
    const rank  = { safe: 0, caution: 1, danger: 2 };
    if ((rank[local] ?? 0) > (rank[status] ?? 0)) status = local;
  }

  const formatTime = (seconds) => {
    if (seconds == null || seconds >= 86400) return null;
    if (seconds < 0) return null;
    if (seconds < 900) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}m ${secs}s`;
    }
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    const hours = Math.floor(seconds / 3600);
    const mins  = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  };

  const statusConfig = {
    safe:    { color: C.green,  icon: "✅", label: "SAFE TO TRADE",         bg: `${C.green}15`,  border: `${C.green}40`  },
    caution: { color: C.yellow, icon: "⚠️", label: "CAUTION — NEWS NEARBY", bg: `${C.yellow}15`, border: `${C.yellow}40` },
    danger:  { color: C.red,    icon: "🚫", label: "NO TRADE ZONE",          bg: `${C.red}15`,    border: `${C.red}40`    },
    unknown: { color: C.yellow, icon: "❔", label: "STATUS UNKNOWN",         bg: `${C.yellow}10`, border: `${C.yellow}40` },
  };
  // Unrecognised status → unknown, never safe.
  const sc = statusConfig[status] || statusConfig.unknown;

  const formatted = formatTime(timeToEvent);

  let detail;
  if (status === "unknown") {
    detail = calError || "Calendar data can't be verified right now. Check an external calendar before trading.";
  } else if (nextHighImpact && formatted) {
    detail = `${nextHighImpact.event} in ${formatted}`;
  } else if (nextHighImpact && timeToEvent != null && timeToEvent > -POST_FIRE_SECS) {
    detail = `${nextHighImpact.event} — releasing now`;
  } else {
    detail = "No high-impact events in the next 45 minutes.";
  }

  return (
    <Card glow={sc.color} style={{ background: sc.bg, border: `1px solid ${sc.border}`, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ fontSize: 24 }}>{sc.icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: sc.color }}>{sc.label}</div>
          <div style={{ fontSize: 11, color: C.sub, marginTop: 4 }}>{detail}</div>
        </div>
      </div>
    </Card>
  );
}
